/**
 * Where an event sits and where a drag puts it: the one home for in-day versus
 * cross-day indexing, drops onto an empty day, and the rewrite of the cached
 * events an optimistic move shows before the server answers (ADR 0009).
 * Structural types keep the module free of api-types coupling so it tests in
 * node.
 */

export interface PlacedEvent {
	id: number
	tripDayIndex: number
	eventIndex: number
}

/** A slot in the day-grouped list: which Trip day, and how far down it. */
export interface Position {
	dayIndex: number
	index: number
}

/** The event patch a placement sends. `eventIndex` is the index the event holds in its new day once it is there. */
export interface PlacementPatch {
	eventId: number
	tripDayIndex: number
	eventIndex: number
}

const byEventIndex = (a: PlacedEvent, b: PlacedEvent) => a.eventIndex - b.eventIndex

/** Every Trip day of the trip in order — empty days included — each holding its events in order. */
export const groupByDay = <E extends PlacedEvent>(events: readonly E[], dayCount: number): E[][] => {
	const days: E[][] = Array.from({ length: dayCount }, () => [])
	for (const event of events) days[event.tripDayIndex]?.push(event)
	days.forEach(day => day.sort(byEventIndex))
	return days
}

/** The slot an event holds in the day-grouped list; null when the list does not have it. */
export const positionOf = (days: readonly PlacedEvent[][], eventId: number): Position | null => {
	for (let dayIndex = 0; dayIndex < days.length; dayIndex++) {
		const index = days[dayIndex].findIndex(e => e.id === eventId)
		if (index !== -1) return { dayIndex, index }
	}
	return null
}

/**
 * The events as they stand once the patch has landed: the moved event in its
 * new slot, and both days it touched renumbered from zero, the way the server
 * renumbers them. An index past the end of the day lands at the end.
 */
export const applyPlacement = <E extends PlacedEvent>(events: readonly E[], patch: PlacementPatch): E[] => {
	const moving = events.find(e => e.id === patch.eventId)
	if (!moving) return [...events]

	const dayWithoutIt = (dayIndex: number) =>
		events.filter(e => e.tripDayIndex === dayIndex && e.id !== moving.id).sort(byEventIndex)
	const target = dayWithoutIt(patch.tripDayIndex)
	target.splice(Math.max(0, Math.min(patch.eventIndex, target.length)), 0, moving)

	const placed = new Map<number, { tripDayIndex: number; eventIndex: number }>()
	if (moving.tripDayIndex !== patch.tripDayIndex) {
		dayWithoutIt(moving.tripDayIndex).forEach((e, eventIndex) =>
			placed.set(e.id, { tripDayIndex: e.tripDayIndex, eventIndex }),
		)
	}
	target.forEach((e, eventIndex) => placed.set(e.id, { tripDayIndex: patch.tripDayIndex, eventIndex }))

	return events
		.map(e => ({ ...e, ...placed.get(e.id) }))
		.sort((a, b) => a.tripDayIndex - b.tripDayIndex || a.eventIndex - b.eventIndex)
}

/**
 * A drop, as the patch to send and the day-grouped list it leaves behind.
 * `drop.index` is the slot the event takes in the target day once it has left
 * its old one. Null when there is nothing to send: no event at the source, no
 * such day, or a drop onto the slot the event already holds.
 */
export const placeEvent = <E extends PlacedEvent>(
	days: readonly E[][],
	source: Position,
	drop: Position,
): { patch: PlacementPatch; days: E[][] } | null => {
	const moving = days[source.dayIndex]?.[source.index]
	const targetDay = days[drop.dayIndex]
	if (!moving || !targetDay) return null

	const sameDay = source.dayIndex === drop.dayIndex
	const lastSlot = sameDay ? targetDay.length - 1 : targetDay.length
	const eventIndex = Math.max(0, Math.min(drop.index, lastSlot))
	if (sameDay && eventIndex === source.index) return null

	const patch = { eventId: moving.id, tripDayIndex: drop.dayIndex, eventIndex }
	return { patch, days: groupByDay(applyPlacement(days.flat(), patch), days.length) }
}
